"use strict";
/**
 * Centralized localStorage utility for the Visual Analyzer application
 * Provides type-safe access to localStorage with error handling and browser compatibility
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.storageOperations = exports.recordingStorage = exports.sendTabStorage = exports.transformTabStorage = exports.messageHistoryStorage = exports.dataListStorage = exports.tabStorage = exports.filterStorage = exports.localStorage = exports.STORAGE_KEYS = void 0;
// Storage keys used throughout the application
exports.STORAGE_KEYS = {
    // App panel settings
    FILTER_SETTINGS: 'visual_analyzer_settings',
    ACTIVE_TAB: 'visual_analyzer_active_tab',
    DATALIST_VISIBILITY: 'visual_analyzer_datalist_visible',
    FILTER_PANEL_OPEN: 'visual_analyzer_filter_panel_open',
    // Transform tab settings
    TRANSFORM_INPUT_VALUE: 'transformTab-inputValue',
    TRANSFORM_OUTPUT_FORMAT: 'transformTab-outputFormat',
    TRANSFORM_MESSAGE_HISTORY: 'transformTab-messageHistory',
    // Send tab settings
    SEND_MESSAGE_HISTORY: 'nmea2000MessageHistory',
    // Recording tab settings
    RECORDING_FORMAT: 'visual-analyzer-recording-format',
};
/**
 * Type-safe localStorage wrapper with error handling
 */
class LocalStorageService {
    /**
     * Check if localStorage is available in the current environment
     */
    isAvailable() {
        try {
            return typeof window !== 'undefined' && window.localStorage !== null;
        }
        catch (e) {
            return false;
        }
    }
    /**
     * Get an item from localStorage with type safety and error handling
     * @param key - The storage key
     * @param defaultValue - Default value to return if item doesn't exist or parsing fails
     * @returns The stored value or default value
     */
    getItem(key, defaultValue) {
        try {
            if (!this.isAvailable()) {
                console.warn(`localStorage not available, returning default value for ${key}`);
                return defaultValue;
            }
            const stored = window.localStorage.getItem(key);
            if (stored === null) {
                return defaultValue;
            }
            return JSON.parse(stored);
        }
        catch (error) {
            console.warn(`Failed to load ${key} from localStorage:`, error);
            return defaultValue;
        }
    }
    /**
     * Set an item in localStorage with error handling
     * @param key - The storage key
     * @param value - The value to store
     * @returns true if successful, false otherwise
     */
    setItem(key, value) {
        try {
            if (!this.isAvailable()) {
                console.warn(`localStorage not available, cannot save ${key}`);
                return false;
            }
            window.localStorage.setItem(key, JSON.stringify(value));
            return true;
        }
        catch (error) {
            console.warn(`Failed to save ${key} to localStorage:`, error);
            return false;
        }
    }
    /**
     * Remove an item from localStorage
     * @param key - The storage key
     * @returns true if successful, false otherwise
     */
    removeItem(key) {
        try {
            if (!this.isAvailable()) {
                console.warn(`localStorage not available, cannot remove ${key}`);
                return false;
            }
            window.localStorage.removeItem(key);
            return true;
        }
        catch (error) {
            console.warn(`Failed to remove ${key} from localStorage:`, error);
            return false;
        }
    }
    /**
     * Clear all localStorage items
     * @returns true if successful, false otherwise
     */
    clear() {
        try {
            if (!this.isAvailable()) {
                console.warn('localStorage not available, cannot clear');
                return false;
            }
            window.localStorage.clear();
            return true;
        }
        catch (error) {
            console.warn('Failed to clear localStorage:', error);
            return false;
        }
    }
    /**
     * Get all keys from localStorage
     * @returns Array of keys
     */
    getAllKeys() {
        try {
            if (!this.isAvailable()) {
                return [];
            }
            const keys = [];
            for (let i = 0; i < window.localStorage.length; i++) {
                const key = window.localStorage.key(i);
                if (key) {
                    keys.push(key);
                }
            }
            return keys;
        }
        catch (error) {
            console.warn('Failed to get localStorage keys:', error);
            return [];
        }
    }
}
// Create and export singleton instance
exports.localStorage = new LocalStorageService();
/**
 * Specialized methods for common use cases
 */
// Filter settings management
exports.filterStorage = {
    save: (settings) => {
        const currentSettings = exports.filterStorage.load();
        const updatedSettings = {
            ...currentSettings,
            ...settings,
            lastSaved: new Date().toISOString(),
        };
        return exports.localStorage.setItem(exports.STORAGE_KEYS.FILTER_SETTINGS, updatedSettings);
    },
    load: () => {
        return exports.localStorage.getItem(exports.STORAGE_KEYS.FILTER_SETTINGS, {
            filter: {},
            doFiltering: false,
            filterOptions: {
                showUnknownProprietaryPGNsOnSeparateLines: false,
                showPgn126208OnSeparateLines: false,
                maxHistorySize: 10,
            },
            lastSaved: new Date().toISOString(),
        });
    },
};
// Tab management
exports.tabStorage = {
    setActiveTab: (tabId) => {
        return exports.localStorage.setItem(exports.STORAGE_KEYS.ACTIVE_TAB, tabId);
    },
    getActiveTab: () => {
        return exports.localStorage.getItem(exports.STORAGE_KEYS.ACTIVE_TAB, null);
    },
};
// DataList visibility
exports.dataListStorage = {
    setVisibility: (visible) => {
        return exports.localStorage.setItem(exports.STORAGE_KEYS.DATALIST_VISIBILITY, visible);
    },
    getVisibility: () => {
        return exports.localStorage.getItem(exports.STORAGE_KEYS.DATALIST_VISIBILITY, true);
    },
};
// Message history management
exports.messageHistoryStorage = {
    save: (key, history) => {
        return exports.localStorage.setItem(key, history);
    },
    load: (key) => {
        return exports.localStorage.getItem(key, []);
    },
    addMessage: (key, message, maxSize = 50) => {
        const history = exports.messageHistoryStorage.load(key);
        history.unshift(message);
        if (history.length > maxSize) {
            history.splice(maxSize);
        }
        return exports.messageHistoryStorage.save(key, history);
    },
};
// Transform tab specific storage
exports.transformTabStorage = {
    setInputValue: (value) => {
        return exports.localStorage.setItem(exports.STORAGE_KEYS.TRANSFORM_INPUT_VALUE, value);
    },
    getInputValue: () => {
        return exports.localStorage.getItem(exports.STORAGE_KEYS.TRANSFORM_INPUT_VALUE, '');
    },
    setOutputFormat: (format) => {
        return exports.localStorage.setItem(exports.STORAGE_KEYS.TRANSFORM_OUTPUT_FORMAT, format);
    },
    getOutputFormat: () => {
        return exports.localStorage.getItem(exports.STORAGE_KEYS.TRANSFORM_OUTPUT_FORMAT, 'canboat-json');
    },
    setMessageHistory: (history) => {
        return exports.messageHistoryStorage.save(exports.STORAGE_KEYS.TRANSFORM_MESSAGE_HISTORY, history);
    },
    getMessageHistory: () => {
        return exports.messageHistoryStorage.load(exports.STORAGE_KEYS.TRANSFORM_MESSAGE_HISTORY);
    },
    addMessageToHistory: (message) => {
        return exports.messageHistoryStorage.addMessage(exports.STORAGE_KEYS.TRANSFORM_MESSAGE_HISTORY, message);
    },
};
// Send tab specific storage
exports.sendTabStorage = {
    setMessageHistory: (history) => {
        return exports.messageHistoryStorage.save(exports.STORAGE_KEYS.SEND_MESSAGE_HISTORY, history);
    },
    getMessageHistory: () => {
        return exports.messageHistoryStorage.load(exports.STORAGE_KEYS.SEND_MESSAGE_HISTORY);
    },
    addMessageToHistory: (message) => {
        return exports.messageHistoryStorage.addMessage(exports.STORAGE_KEYS.SEND_MESSAGE_HISTORY, message);
    },
};
// Recording tab specific storage
exports.recordingStorage = {
    setFormat: (format) => {
        return exports.localStorage.setItem(exports.STORAGE_KEYS.RECORDING_FORMAT, format);
    },
    getFormat: () => {
        return exports.localStorage.getItem(exports.STORAGE_KEYS.RECORDING_FORMAT, 'passthrough');
    },
};
// Generic storage operations for backward compatibility
exports.storageOperations = {
    saveFilterSettings: exports.filterStorage.save,
    loadFilterSettings: exports.filterStorage.load,
    saveDataListVisibility: exports.dataListStorage.setVisibility,
    loadDataListVisibility: exports.dataListStorage.getVisibility,
    saveActiveTab: exports.tabStorage.setActiveTab,
    loadActiveTab: exports.tabStorage.getActiveTab,
};
exports.default = exports.localStorage;
